import React, { useState } from "react";
import { Modal, Button, Form, InputGroup, ProgressBar } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faKey,
  faEye,
  faEyeSlash,
  faLock,
  faWandMagicSparkles,
  faCopy,
  faCheck,
} from "@fortawesome/free-solid-svg-icons";
import { InvisilatorService } from "Services/InvisilatorService";
import { toast } from "react-toastify";

interface Props {
  show: boolean;
  onClose: () => void;
  invigilator: any;
  onSubmit?: (password: string) => void;
}

export default function ResetPasswordModal({
  show,
  onClose,
  invigilator,
  onSubmit,
}: Props) {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [showConfirmPass, setShowConfirmPass] = useState(false);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(false);

  const getStrength = (pass: string) => {
    let score = 0;
    if (pass.length >= 6) score++;
    if (/[A-Z]/.test(pass)) score++;
    if (/[0-9]/.test(pass)) score++;
    if (/[^A-Za-z0-9]/.test(pass)) score++;

    if (score <= 1) return { label: "Weak", variant: "danger", value: 30 };
    if (score === 2 || score === 3)
      return { label: "Medium", variant: "warning", value: 60 };
    return { label: "Strong", variant: "success", value: 100 };
  };

  const strength = getStrength(password);

  const generatePassword = () => {
    const chars =
      "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#";
    let newPass = "";
    for (let i = 0; i < 10; i++) {
      newPass += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setPassword(newPass);
    setConfirmPassword(newPass);
    setError("");
  };

  const copyPassword = async () => {
    if (!password) return;
    await navigator.clipboard.writeText(password);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const handleSubmit = async () => {
    if (!password || !confirmPassword) {
      setError("Please fill all fields");
      return;
    }

    if (password.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }

    if (password !== confirmPassword) {
      setError("Please enter same password");
      return;
    }

    try {
      setLoading(true);

      const res = await InvisilatorService.resetPassword(
        invigilator._id,
        { password }
      );

      if (res?.status === 200) {
        toast.success("Password reset successfully");
        onSubmit?.(password);
        setPassword("");
        setConfirmPassword("");
        onClose();
      }
    } catch (err) {
      toast.error("Failed to reset password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={onClose} centered backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title className="fw-bold">Reset Password</Modal.Title>
      </Modal.Header>

      <Modal.Body className="bg-light">
        <div className="bg-white rounded p-4 shadow-sm">

          <p className="text-muted mb-4">
            Reset password for:
            <span className="fw-semibold ms-1 text-dark">
              {invigilator?.name}
            </span>
          </p>

          <Form.Group className="mb-3">
            <Form.Label className="fw-semibold">
              <FontAwesomeIcon icon={faKey} className="me-2 text-primary" />
              New Password
            </Form.Label>

            <InputGroup>
              <Form.Control
                type={showPass ? "text" : "password"}
                placeholder="Enter new password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />

              <Button
                variant="outline-secondary"
                onClick={() => setShowPass(!showPass)}
              >
                <FontAwesomeIcon icon={showPass ? faEyeSlash : faEye} />
              </Button>

              <Button variant="outline-primary" onClick={copyPassword}>
                <FontAwesomeIcon icon={copied ? faCheck : faCopy} />
              </Button>
            </InputGroup>

            {password && (
              <div className="mt-2">
                <ProgressBar
                  now={strength.value}
                  variant={strength.variant}
                  style={{ height: "6px" }}
                />
                <small className={`text-${strength.variant} fw-semibold`}>
                  {strength.label} password
                </small>
              </div>
            )}
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className="fw-semibold">
              <FontAwesomeIcon icon={faLock} className="me-2 text-success" />
              Confirm New Password
            </Form.Label>

            <InputGroup>
              <Form.Control
                type={showConfirmPass ? "text" : "password"}
                placeholder="Confirm new password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />

              <Button
                variant="outline-secondary"
                onClick={() => setShowConfirmPass(!showConfirmPass)}
              >
                <FontAwesomeIcon icon={showConfirmPass ? faEyeSlash : faEye} />
              </Button>
            </InputGroup>
          </Form.Group>

          <div className="d-flex justify-content-between align-items-center mb-3">
            <Button
              variant="outline-primary"
              size="sm"
              onClick={generatePassword}
            >
              <FontAwesomeIcon icon={faWandMagicSparkles} className="me-2" />
              Generate Strong Password
            </Button>
          </div>

          {error && <div className="text-danger fw-semibold">{error}</div>}
        </div>
      </Modal.Body>

      <Modal.Footer className="bg-white">
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>

        <Button variant="primary" onClick={handleSubmit} disabled={loading}>
          {loading ? "Updating..." : "Reset Password"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
